import type { UserRole } from "@/dashboard/features/userManagement/lib/schema";

/** Roles whose permissions an account can edit. Admin always has every permission. */
export const EDITABLE_ROLES = ["manager", "member", "analytics_viewer"] as const;
export type EditableRole = (typeof EDITABLE_ROLES)[number];

export function isEditableRole(role: UserRole): role is EditableRole {
  return EDITABLE_ROLES.some(editableRole => editableRole === role);
}

export const PermissionGroup = {
  TEAM: "Team",
  INBOX: "Inbox",
  OPERATIONS: "Operations",
  EMERGENCY: "Emergency",
  ANALYTICS: "Analytics",
  CAMPAIGNS: "Campaigns",
  PHONE_SYSTEM: "Phone system",
} as const;
export type PermissionGroup = (typeof PermissionGroup)[keyof typeof PermissionGroup];

/** One row of the roles grid. `view` and `edit` rows are areas; `action` rows are a single switch. */
export type PermissionLevel = "view" | "edit" | "action";

type PermissionDefinition = {
  row: string;
  level: PermissionLevel;
  label: string;
  group: PermissionGroup;
  defaultRoles: readonly EditableRole[];
  /** Real tenant admins only: netic_view / netic_ds do not inherit it from their admin-level rank. */
  excludesNeticRoles?: boolean;
  /** Admin-only checks that never appear in the grid and cannot be stored on a role. */
  hidden?: boolean;
  /** What each level of the row covers, when the level names alone would mislead. */
  hint?: string;
};

const MANAGER = ["manager"] as const;
const MANAGER_AND_MEMBER = ["manager", "member"] as const;

// Defaults reproduce today's access; test/dashboard/lib/auth/permissions.test.ts pins each one.
// A row's view defaults always include its edit defaults, because edit implies view.
export const PERMISSIONS = {
  "members.manage": {
    row: "members",
    level: "action",
    label: "Add, edit and remove members",
    group: PermissionGroup.TEAM,
    defaultRoles: [],
  },
  "account.billing": {
    row: "billing",
    level: "action",
    label: "Billing and account settings",
    group: PermissionGroup.TEAM,
    defaultRoles: [],
    excludesNeticRoles: true,
  },
  "notifications.systemStatus.manage": {
    row: "systemStatus",
    level: "action",
    label: "System status emails",
    group: PermissionGroup.TEAM,
    defaultRoles: [],
  },
  "inbox.view": {
    row: "inbox",
    level: "view",
    label: "Inbox, contact center and everyday pages",
    group: PermissionGroup.INBOX,
    defaultRoles: MANAGER_AND_MEMBER,
  },
  "folders.view": {
    row: "folders",
    level: "view",
    label: "Folders and folder rules",
    group: PermissionGroup.INBOX,
    defaultRoles: [],
  },
  "folders.edit": {
    row: "folders",
    level: "edit",
    label: "Folders and folder rules",
    group: PermissionGroup.INBOX,
    defaultRoles: [],
  },
  "hoursAndTransfers.view": {
    row: "hoursAndTransfers",
    level: "view",
    label: "Hours, holidays and transfers",
    group: PermissionGroup.OPERATIONS,
    defaultRoles: MANAGER_AND_MEMBER,
  },
  "hoursAndTransfers.edit": {
    row: "hoursAndTransfers",
    level: "edit",
    label: "Hours, holidays and transfers",
    group: PermissionGroup.OPERATIONS,
    defaultRoles: MANAGER_AND_MEMBER,
  },
  "transfers.lineNumbers.edit": {
    row: "transferLineNumbers",
    level: "action",
    label: "Text-line transfer and scheduler display numbers",
    group: PermissionGroup.OPERATIONS,
    defaultRoles: [],
    hidden: true,
  },
  "serviceArea.view": {
    row: "serviceArea",
    level: "view",
    label: "Service area (zip codes and skills)",
    group: PermissionGroup.OPERATIONS,
    defaultRoles: MANAGER_AND_MEMBER,
  },
  "serviceArea.edit": {
    row: "serviceArea",
    level: "edit",
    label: "Service area (zip codes and skills)",
    group: PermissionGroup.OPERATIONS,
    defaultRoles: MANAGER_AND_MEMBER,
  },
  "technicians.view": {
    row: "technicians",
    level: "view",
    label: "Technicians, shifts and time off",
    group: PermissionGroup.OPERATIONS,
    defaultRoles: MANAGER,
  },
  "technicians.edit": {
    row: "technicians",
    level: "edit",
    label: "Technicians, shifts and time off",
    group: PermissionGroup.OPERATIONS,
    defaultRoles: MANAGER,
  },
  "arrivalWindows.view": {
    row: "arrivalWindows",
    level: "view",
    label: "Arrival windows",
    group: PermissionGroup.OPERATIONS,
    defaultRoles: MANAGER_AND_MEMBER,
  },
  "arrivalWindows.edit": {
    row: "arrivalWindows",
    level: "edit",
    label: "Arrival windows",
    group: PermissionGroup.OPERATIONS,
    defaultRoles: MANAGER,
  },
  "schedulingRules.view": {
    row: "schedulingRules",
    level: "view",
    label: "Utilization and scheduling rules",
    group: PermissionGroup.OPERATIONS,
    defaultRoles: MANAGER_AND_MEMBER,
  },
  "schedulingRules.edit": {
    row: "schedulingRules",
    level: "edit",
    label: "Utilization and scheduling rules",
    group: PermissionGroup.OPERATIONS,
    defaultRoles: MANAGER_AND_MEMBER,
  },
  "utilization.view": {
    row: "utilization",
    level: "view",
    label: "Utilization board and insights",
    group: PermissionGroup.OPERATIONS,
    defaultRoles: MANAGER,
  },
  "emergencyContacts.view": {
    row: "emergencyContacts",
    level: "view",
    label: "Emergency contacts, groups and on-call schedule",
    group: PermissionGroup.EMERGENCY,
    defaultRoles: MANAGER_AND_MEMBER,
  },
  "emergencyContacts.edit": {
    row: "emergencyContacts",
    level: "edit",
    label: "Emergency contacts, groups and on-call schedule",
    group: PermissionGroup.EMERGENCY,
    defaultRoles: MANAGER_AND_MEMBER,
  },
  "emergencySettings.view": {
    row: "emergencySettings",
    level: "view",
    label: "Emergency settings, criteria and fees",
    group: PermissionGroup.EMERGENCY,
    defaultRoles: MANAGER_AND_MEMBER,
  },
  "emergencySettings.edit": {
    row: "emergencySettings",
    level: "edit",
    label: "Emergency settings, criteria and fees",
    group: PermissionGroup.EMERGENCY,
    defaultRoles: MANAGER,
  },
  "analytics.view": {
    row: "analytics",
    level: "view",
    label: "Analytics",
    group: PermissionGroup.ANALYTICS,
    defaultRoles: ["manager", "analytics_viewer"],
  },
  "performanceAtlas.view": {
    row: "performanceAtlas",
    level: "view",
    label: "Performance Atlas",
    group: PermissionGroup.ANALYTICS,
    defaultRoles: MANAGER,
  },
  "reports.export": {
    row: "reports",
    level: "action",
    label: "Export reports and view the audit trail",
    group: PermissionGroup.ANALYTICS,
    defaultRoles: MANAGER,
  },
  "callScoring.view": {
    row: "callScoring",
    level: "view",
    label: "Call scoring",
    group: PermissionGroup.ANALYTICS,
    defaultRoles: MANAGER,
    hint: "View: cross-brand score summaries only, not the Call Scoring pages. Edit: the Call Scoring pages, to review calls, change scores and manage rubrics.",
  },
  "callScoring.edit": {
    row: "callScoring",
    level: "edit",
    label: "Call scoring",
    group: PermissionGroup.ANALYTICS,
    defaultRoles: [],
  },
  "campaigns.view": {
    row: "campaigns",
    level: "view",
    label: "Campaigns, audiences and digital leads",
    group: PermissionGroup.CAMPAIGNS,
    defaultRoles: MANAGER,
  },
  "campaigns.edit": {
    row: "campaigns",
    level: "edit",
    label: "Campaigns, audiences and digital leads",
    group: PermissionGroup.CAMPAIGNS,
    defaultRoles: MANAGER,
  },
  "campaigns.optInGate.disable": {
    row: "campaignOptInGate",
    level: "action",
    label: "Turn off the promotional opt-in gate",
    group: PermissionGroup.CAMPAIGNS,
    defaultRoles: [],
    excludesNeticRoles: true,
    hidden: true,
  },
  "phoneSystem.view": {
    row: "phoneSystem",
    level: "view",
    label: "Numbers, routing and tracking",
    group: PermissionGroup.PHONE_SYSTEM,
    defaultRoles: MANAGER,
  },
  "phoneSystem.edit": {
    row: "phoneSystem",
    level: "edit",
    label: "Numbers, routing and tracking",
    group: PermissionGroup.PHONE_SYSTEM,
    defaultRoles: [],
  },
} as const satisfies Record<string, PermissionDefinition>;

export type PermissionKey = keyof typeof PERMISSIONS;

/** One permission, or a list where holding any one of them is enough. */
export type PermissionRequirement = PermissionKey | readonly PermissionKey[];

export const PERMISSION_KEYS = Object.keys(PERMISSIONS) as PermissionKey[];

/** Permissions an account can store on a role; hidden admin-only checks are excluded. */
export const GRANTABLE_PERMISSION_KEYS = PERMISSION_KEYS.filter(key => {
  const definition: PermissionDefinition = PERMISSIONS[key];
  return !definition.hidden;
});

/** Check whether a value names a permission an account can store on a role. */
export function isGrantablePermissionKey(value: string): value is PermissionKey {
  return GRANTABLE_PERMISSION_KEYS.some(key => key === value);
}

/** Get the view permission that an edit permission implies, if its row has one. */
export function impliedViewFor(permission: PermissionKey): PermissionKey | null {
  const definition: PermissionDefinition = PERMISSIONS[permission];
  if (definition.level !== "edit") {
    return null;
  }
  return (
    PERMISSION_KEYS.find(
      key => PERMISSIONS[key].row === definition.row && PERMISSIONS[key].level === "view"
    ) ?? null
  );
}

/** Add the view permission each edit permission implies, so a stored role never holds edit alone. */
export function withImpliedViews(permissions: readonly PermissionKey[]): PermissionKey[] {
  const result = new Set(permissions);
  for (const permission of permissions) {
    const view = impliedViewFor(permission);
    if (view) {
      result.add(view);
    }
  }
  return PERMISSION_KEYS.filter(key => result.has(key));
}

export function toPermissionList(requirement: PermissionRequirement): readonly PermissionKey[] {
  return typeof requirement === "string" ? [requirement] : requirement;
}

export type PermissionRow = {
  row: string;
  label: string;
  group: PermissionGroup;
  hint?: string;
  /** The permissions behind each level the row offers, from lowest to highest. */
  levels: { level: PermissionLevel; key: PermissionKey }[];
};

/** Describe the grid: one entry per row, in catalogue order, without hidden checks. */
export function listPermissionRows(): PermissionRow[] {
  const rows: PermissionRow[] = [];
  for (const key of GRANTABLE_PERMISSION_KEYS) {
    const definition: PermissionDefinition = PERMISSIONS[key];
    const existing = rows.find(candidate => candidate.row === definition.row);
    if (existing) {
      existing.levels.push({ level: definition.level, key });
    } else {
      rows.push({
        row: definition.row,
        label: definition.label,
        group: definition.group,
        hint: definition.hint,
        levels: [{ level: definition.level, key }],
      });
    }
  }
  return rows;
}

const NETIC_ROLES: ReadonlySet<UserRole> = new Set(["netic_view", "netic_ds"]);

/** Check whether a tenant role grants a permission under the default grid. */
export function roleHasPermission(role: UserRole, permission: PermissionKey): boolean {
  const definition: PermissionDefinition = PERMISSIONS[permission];
  if (role === "admin") {
    return true;
  }
  if (NETIC_ROLES.has(role)) {
    return !definition.excludesNeticRoles;
  }
  return definition.defaultRoles.some(defaultRole => defaultRole === role);
}
